import { useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ValueFormatterParams, CellClassParams, ICellRendererParams, RowDragMoveEvent } from 'ag-grid-community';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import PriorityQueries, { IReorderPrioritiesProps } from './PriorityQueries';
import { format, isWithinInterval, parseISO, startOfDay } from 'date-fns';
import { renderUtilisation, renderStatus, renderPriorityGridActions } from '../../components/datagrid/ag-grid';
import { EmployeeWithPrioritiesDto, PriorityItemDto } from '../../api';
import { Grid, IconButton, Typography } from '@mui/material';
import CreatePriorityButton from './CreatePriorityButton';
import '../../styles/agGridStyles.css';
import { useQuery } from 'react-query';
import enNZ from 'date-fns/esm/locale/en-NZ/index.js'; // TODO: can defining a locale be done in just one place with a provider or similar?
import { employeeFromDto } from '../../models/Employee';
import ReassignPriorityPopup from '../../components/form/popups/ReassignPriorityPopup';
import { AccountCircleOutlined } from '@mui/icons-material';
import ConfirmProfileDialog, { IConfirmProfileDialog } from '../../components/form/controls/ConfirmProfileDialog';
import { startOfToday } from 'date-fns/esm';
import { green } from '@mui/material/colors';

interface IEmployeePriorityGridProps {
  employee: EmployeeWithPrioritiesDto;
}

/**
 * Defines rules for applying CSS classes to the start and end dates.
 */
const dateCellRules = {
  'project-active': (params: CellClassParams) => {
    const today = startOfToday();
    const startDate = startOfDay(parseISO(params.data.startDate));
    const endDate = startOfDay(parseISO(params.data.endDate));
    return isWithinInterval(today, { start: startDate, end: endDate });
  },
};

function EmployeePriorityGrid(props: IEmployeePriorityGridProps) {
  const { employee } = props;
  const { mutateReorderPriorities } = PriorityQueries();
  const [draggedRowInitialIndex, setDraggedRowInitialIndex] = useState<number | null>(null);
  const [columnDefs, setColumnDefs] = useState(getColumnDefs());
  const [rightClickedPriority, setRightClickedPriority] = useState<PriorityItemDto | undefined>(undefined);
  const [isRightClickDialogOpen, setIsRightClickDialogOpen] = useState(false);
  const [profileDialog, setProfileDialog] = useState({
    isOpen: false,
    title: '',
    content: '',
  } as IConfirmProfileDialog);

  /**
   * Formats the date to dd/mm/yyyy format
   * @param params ValueFormatterParams
   * @returns Date string in dd/mm/yyyy format
   */
  function dateFormatter(params: ValueFormatterParams) {
    const date: Date = parseISO(params.value);
    const formattedDate = format(date, 'dd/MM/yyyy', { locale: enNZ });
    return formattedDate;
  }

  const handleProfile = async () => {
    if (employee?.profileLink) {
      window.open(employee?.profileLink);
    } else {
      setProfileDialog({
        isOpen: true,
        title: 'Employee does not have a profile link.',
        content: 'Would you like to go to the profile folder?',
        onConfirm: closeProfileDialog,
      } as IConfirmProfileDialog);
    }
  };

  /**
   * Closes the profile dialog
   */
  const closeProfileDialog = () => {
    setProfileDialog({
      ...profileDialog,
      isOpen: false,
    });
    window.open(
      'https://datacomunity.sharepoint.com/:f:/r/Teams/SoftwareBuild/Shared%20Documents/Profiles?csf=1&web=1&e=W6f0Yf'
    );
  };

  /** Refresh column headers when the employee's total utilisation changes */
  useQuery(`${employee.currentUtilisation}`, () => {
    setColumnDefs(getColumnDefs());
  });

  /**
   * Gets the column header definition for the grid
   */
  function getColumnDefs() {
    return [
      {
        rowDrag: true,
        width: 50,
      },
      {
        headerName: 'Client',
        field: 'project.clientName',
        sortable: true,
        filter: true,
        wrapText: true,
        autoHeight: true,
        width: 180,
      },
      {
        headerName: 'Project',
        field: 'project.name',
        sortable: true,
        filter: true,
        wrapText: true,
        autoHeight: true,
        width: 160,
      },
      {
        headerName: 'Description',
        field: 'description',
        sortable: true,
        filter: true,
        wrapText: true,
        autoHeight: true,
        width: 180,
      },
      {
        colId: 'totalUtilisation',
        headerName: `Utilisation (${employee.currentUtilisation}%)`,
        field: 'utilisation',
        sortable: true,
        filter: true,
        wrapText: true,
        autoHeight: true,
        width: 140,
        cellRenderer: renderUtilisation,
      },
      {
        headerName: 'Start Date',
        field: 'startDate',
        sortable: true,
        filter: true,
        wrapText: true,
        autoHeight: true,
        width: 120,
        valueFormatter: dateFormatter,
        cellClassRules: dateCellRules,
      },
      {
        headerName: 'End Date',
        field: 'endDate',
        sortable: true,
        filter: true,
        wrapText: true,
        autoHeight: true,
        width: 120,
        valueFormatter: dateFormatter,
        cellClassRules: dateCellRules,
      },
      {
        headerName: 'Status',
        field: 'status',
        sortable: true,
        filter: true,
        wrapText: true,
        autoHeight: true,
        width: 125,
        cellRenderer: renderStatus,
      },
      {
        headerName: 'Actions',
        sortable: false,
        filter: false,
        autoHeight: true,
        width: 150,
        cellRenderer: (params: ICellRendererParams) => renderPriorityGridActions(params, employeeFromDto(employee)),
      },
    ];
  }

  /**
   * Handles row reordering when a drag event has ended.
   * @param event
   */
  function onRowDragEnd(event: RowDragMoveEvent) {
    const finalRowIndex = event.node.rowIndex;

    if (finalRowIndex !== draggedRowInitialIndex) {
      const rows = event.api.getModel();

      let reorderedPriorityList: PriorityItemDto[] = [];
      rows.forEachNode(n => {
        if (n.rowIndex !== null) {
          let updatedPriority: PriorityItemDto = { ...(n.data as PriorityItemDto) };
          updatedPriority.order = n.rowIndex;
          reorderedPriorityList.push(updatedPriority);
        }
      });

      mutateReorderPriorities({
        employeeId: employee.id,
        priorities: reorderedPriorityList,
      } as IReorderPrioritiesProps);
    }
  }

  return (
    <>
      <ConfirmProfileDialog confirmProfileDialog={profileDialog} setConfirmProfileDialog={setProfileDialog} />
      <Grid container display="flex" direction="column" justifyContent={'center'} alignItems="center" spacing={1}>
        <Grid item xs={12}>
          <div className="ag-theme-alpine" style={{ width: 1227 }}>
            <Typography
              variant="h4"
              display="inline"
              marginBottom={0.5}
            >{`${employee.firstName} ${employee.lastName}`}</Typography>
            <IconButton
              aria-label="open profile"
              sx={{ paddingBottom: '1.5%', color: employee.profileLink ? green[600] : 'black' }}
              component="span"
              onClick={handleProfile}
            >
              <AccountCircleOutlined />
            </IconButton>
            <hr style={{ marginBottom: 10, marginTop: 0 }} />

            {employee.priorities && employee.priorities.length > 0 && (
              <AgGridReact
                rowData={employee.priorities}
                columnDefs={columnDefs}
                domLayout="autoHeight"
                rowDragManaged={true}
                rowDragEntireRow={true}
                rowHeight={55}
                onRowDragEnter={event => setDraggedRowInitialIndex(event.node.rowIndex)}
                onRowDragEnd={onRowDragEnd}
                preventDefaultOnContextMenu={true}
                suppressContextMenu={true}
                onCellContextMenu={e => {
                  setRightClickedPriority(e.data as PriorityItemDto);
                  setIsRightClickDialogOpen(true);
                }}
                suppressMoveWhenRowDragging={true}
              />
            )}
          </div>
        </Grid>
        <Grid item>
          <CreatePriorityButton employee={employeeFromDto(employee)} order={employee.priorities?.length ?? 0} />
        </Grid>
      </Grid>
      {rightClickedPriority && (
        <ReassignPriorityPopup
          isOpen={isRightClickDialogOpen}
          setIsOpen={setIsRightClickDialogOpen}
          priority={rightClickedPriority}
          onSuccess={() => {
            setIsRightClickDialogOpen(false);
            setRightClickedPriority(undefined);
          }}
        />
      )}
    </>
  );
}

export default EmployeePriorityGrid;
